<script>
  import { onDestroy, onMount } from 'svelte';
  import { ignore, makeWebsiteRequest } from '../../Common/tools';
  import { slide } from 'svelte/transition';
  import TextEditor from '../../Common/TextEditor.svelte';
  import { External, M } from '../../external';
  import API from '../../main';
  import TextAssistIcon from '../../Common/TextAssistIcon.svelte';

  export let id = 0,
    newProductType = External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT,
    onLoad = ignore,
    onSave = null,
    inPopup = false;

  let showTextSuggestionsBtn = null;
  let product = null;
  let changed = false;
  let saving = false;
  let productFileDisplayName = '';
  let selectedMemberGroupNames = [];
  let hasSubscription = false;
  let cheapestVariation = 0;
  let showingSingleVariantOptions = true;
  let isTrackingInventory = false;
  let hasThankYouPage = false;
  let thankYouPageFriendlyText = '';
  let editorType = 'simple';
  let builderMarkup = null;
  let textEditorMarkup = null;
  let grid = null;

  const textEditorOptions = {
    forceFixedPosition: false,
    addExtraButtons: false,
    addImageButton: false,
    addMarketingTags: false,
    useEmailFontsOnly: false,
    focused: false,
    textAssistOptions: {
      hasExtraBtn: true,
      extraBtnText: M.TEXT_ASSIST_DESCRIPTION_FROM_TITLE,
      extraBtnType: 'PRODUCT_DESCRIPTION',
      decoratePhrase: (phrase, type) => {
        if (type === 'PRODUCT_DESCRIPTION') {
          return product.title || phrase;
        }
        return phrase;
      }
    }
  };

  const TEXT_WIDGET_TYPES = ['TextWidget', 'HeadingWidget'];

  const defaultTextContent = '<p style="color: #888; line-height: 1.6; font-size: 15px;">' + M.LOREM_IPSUM + ' ' + M.LOREM_IPSUM + '</p>';

  let getLocaleSettings = () => {
    let settings = External.WebsiteSettings && External.WebsiteSettings.getSettings ? (External.WebsiteSettings.getSettings() || {}) : {};
    let defaultLocale = settings.websiteLanguage || (External.Website ? External.Website.websiteLanguage : '') || 'en';
    let locales = Array.isArray(settings.websiteLocales) ? settings.websiteLocales.slice(0) : [];
    if (locales.indexOf(defaultLocale) < 0) {
      locales.unshift(defaultLocale);
    }
    let currentLocale = External.Website ? (External.Website.currentEditLocale || External.Website.defaultEditLocale || defaultLocale) : defaultLocale;
    if (locales.indexOf(currentLocale) < 0) {
      currentLocale = defaultLocale;
    }
    return { currentLocale, defaultLocale };
  };

  let getActiveLocale = () => {
    return getLocaleSettings().currentLocale;
  };

  let init = (data) => {
    product = External.mergeUnique(data.product, {
      type: External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT,
      title: '',
      description: {
        simple: true,
        text: defaultTextContent
      },
      url: '',
      hidden: false,
      seoDescription: '',
      images: [],
      price: 0,
      salePrice: 0,
      onSale: false,
      sku: '',
      quantity: '',
      weight: 1,
      width: 0,
      height: 0,
      length: 0,
      categories: [],
      variations: [],
      combinations: [],
      file: '',
      additions: null
    });
    product.additions = product.additions || {};
    let localeSettings = getLocaleSettings();
    if (product.additions.locales && localeSettings.currentLocale !== localeSettings.defaultLocale) {
      let localeData = product.additions.locales[localeSettings.currentLocale] || {};
      if (localeData.title) {
        product.title = localeData.title;
      }
      if (localeData.seoDescription) {
        product.seoDescription = localeData.seoDescription;
      }
      if (localeData.seoTitle) {
        product.additions.seoTitle = localeData.seoTitle;
      }
      if (localeData.description) {
        product.description = localeData.description;
      }
    }
    hasSubscription = product.additions.subscription && product.additions.subscription.period;
    refreshInventoryCbx();
    refreshThankYouPageCbx();
    onProductTypeChange();

    if (data.markup && !product.description.simple) {
      builderMarkup = data.markup;
      editorType = 'complex';
      if (switchToSimpleEditor()) {
        editorType = 'simple';
      }
    } else {
      textEditorMarkup = product.description.text || defaultTextContent;
      if (!textEditorMarkup.trim().startsWith('<')) {
        textEditorMarkup = '<p style="color: #888; line-height: 1.5">' + textEditorMarkup + '</p>';
      }
      editorType = 'simple';
    }
  };

  let switchToSimpleEditor = () => {
    let textWidgets = findWidgets(product.description.widgets, TEXT_WIDGET_TYPES);
    let allWidgets = findWidgets(product.description.widgets, []);
    if (textWidgets.length === allWidgets.length) {
      textEditorMarkup = textWidgets.map((widget) => widget.content).join('<p></p>');
      return true;
    }
    return false;
  };

  let onTextEditorChange = (content) => {
    if (content !== textEditorMarkup) {
      textEditorMarkup = content;
      setChanged();
    }
  };

  let setupGrid = (node) => {
    let gridContainer = External.$(node);
    grid = External.Grid(gridContainer, {
      disableHistoryKeyboardBindings: true,
      forceDesktopMode: true,
      widgetsOnly: true,
      showNewWidgetUiAtRight: inPopup,
      hasAnchorPoints: false,
      widgetFilter: (item) => {
        if (
          (item.type === External.WIDGET_TYPE.CATEGORY && item.value === M.WIDGET_CATEGORY_COMPLEX) ||
          (item.type === External.WIDGET_TYPE.WIDGET && item.value === 'Container') ||
          item.type === External.WIDGET_TYPE.COMPLEX_WIDGET
        ) {
          return false;
        }
        return true;
      },
      onChange: setChanged
    });
    return {
      destroy: () => {
        grid.destroy();
        grid = null;
        gridContainer = null;
      }
    };
  };

  export function undo() {
    if (grid) {
      grid.undo();
    }
  }

  export function redo() {
    if (grid) {
      grid.redo();
    }
  }

  export function isChanged() {
    return changed;
  }

  export async function save() {
    await saveProduct(false, false);
  }

  /**
   * Iterate over the builder widgets to extract text widgets. They'll be used for the simple editor
   *
   */
  let findWidgets = (container, widgetTypes) => {
    let widgets = [];
    if (container === null) {
      return widgets;
    } else if (Array.isArray(container)) {
      container.forEach((container) => {
        widgets = [...widgets, ...findWidgets(container, widgetTypes)];
      });
    } else if (container.widgets) {
      widgets = container.widgets.filter((widget) => widgetTypes.length === 0 || widgetTypes.indexOf(widget.type) > -1);
    } else if (container.rows) {
      container.rows.forEach((row) => {
        widgets = [...widgets, ...findWidgets(row, widgetTypes)];
      });
    } else if (container.columns) {
      container.columns.forEach((column) => {
        widgets = [...widgets, ...findWidgets(column, widgetTypes)];
      });
    }
    return widgets;
  };

  let setDescriptionType = (type) => {
    if (type === 'simple') {
      if (!textEditorMarkup || textEditorMarkup === defaultTextContent) {
        if (product.description.widgets) {
          let widgets = findWidgets(product.description.widgets, TEXT_WIDGET_TYPES);
          textEditorMarkup = widgets.map((widget) => widget.content).join('<p></p>');
        }
      }
    } else {
      if (!builderMarkup && textEditorMarkup) {
        let markup = External.$(External.Templates.get('storeProductDefaultContentMarkup'));
        markup.find('.text-widget-content').html(textEditorMarkup);
        builderMarkup = markup.wrap('<div/>').parent().html();
      }
    }
    editorType = type;
    setChanged();
  };

  let refreshInventoryCbx = () => {
    isTrackingInventory = false;
    if (!showingSingleVariantOptions && product.combinations.length) {
      isTrackingInventory = product.combinations.filter((comb) => (comb.quantity !== null) && (comb.quantity !== '')).length > 0;
    } else {
      isTrackingInventory = product.quantity !== null && product.quantity !== '';
    }
  };

  let refreshThankYouPageCbx = () => {
    hasThankYouPage = !!product.additions.thankyouPage;
    thankYouPageFriendlyText = External.LinkPicker.getLinkDisplayText(product.additions.thankyouPage || '');
  };

  let setThankYouPage = () => {
    External.Website.selectPage({
      allowEmptyCaption: true,
      allowFunnelStepPicking: false,
      selected: {
        link: product.additions.thankyouPage || '',
        target: '_self',
        caption: ''
      },
      onSelect: function(result) {
        product.additions.thankyouPage = result.link;
        refreshThankYouPageCbx();
        setChanged();
      }
    });
  };

  let setUrl = () => {
    product.url = External.Tools.urlFromString(product.url, product.id < 1).trim();
    setChanged();
  };

  let setChanged = (setChanged) => {
    changed = setChanged !== false;
    External.CPanel.setChanged(changed);
  };

  let onTitleChange = () => {
    if (product.id < 1) {
      product.url = product.title;
      setUrl();
    }
    setChanged();
  };

  let selectFile = () => {
    External.FileManager.selectFile({
      listImagesOnly: false,
      onCompleteCallback: function(fileBase, fileName, displayName) {
        productFileDisplayName = displayName;
        product.file = External.getFileFromBase(fileBase) + fileName;
        setChanged();
      }
    });
  };

  let selectMemberGroups = () => {
    product.additions.memberGroups = product.additions.memberGroups || [];
    let selected = product.additions.memberGroups.map((group) => External.Website.getMemberGroupById(group)).filter((group) => group !== null);

    External.Tools.chooseMultiple({
      title: M.SELECT_MEMBER_GROUPS,
      message: '',
      noItemsMessage: M.NO_MEMBER_GROUPS,
      items: External.Website.memberGroups,
      selectedItems: selected,
      getItemId: (group) => {
        return group.id;
      },
      getItemText: (group) => {
        return group.name;
      },
      isModal: true,
      onSelect: (groups) => {
        product.additions.memberGroups = groups.map((group) => group.id);
        selectedMemberGroupNames = groups.map((group) => group.name);
        setChanged();
      }
    });
  };

  let editCategories = async () => {
    let result = await makeWebsiteRequest({
      action: 'getStoreCategories',
      data: {
        locale: getActiveLocale()
      }
    });

    let tree = External.Tools.buildTree({
      items: result.data,
      selectedItems: product.categories
    });

    External.Tools.treePicker({
      title: M.SELECT_STORE_PRODUCT_CATEGORIES,
      allowNewItems: true,
      items: tree,
      selectChildrenOnSelect: false,
      unselectChildrenOnUnselect: true,
      selectOnlyInnermostCheckedItems: true,
      addExpandBtn: false,
      width: 500,
      onSelect: function(categories) {
        product.categories = categories;
        setChanged();
      },
      onAddItem: function(parent, callback) {
        let order;
        if (parent && parent.childNodes && parent.childNodes.length) {
          order = parent.childNodes[parent.childNodes.length - 1].order + 1;
        } else {
          order = tree.length === 0 ? 1 : (tree[tree.length - 1].order + 1);
        }

        API.Store.Products.addNewProductCategory(parent, order, (category) => {
          callback({
            selected: true,
            name: category.name,
            id: category.id,
            childNodes: []
          });
          callback = null;
        });
      }
    });
  };

  let pickImages = () => {
    let images = product.images || [];
    External.UITools.pickImages({
      images: images,
      onClose: (newItems) => {
        let changed = images.length !== newItems.length;
        if (!changed) {
          for (let idx = 0; idx < images.length; ++idx) {
            let oldImage = images[idx];
            let newImage = newItems[idx];
            if (oldImage.name !== newImage.name || oldImage.base !== newImage.base) {
              changed = true;
              break;
            }
          }
        }
        if (changed) {
          product.images = newItems;
          setChanged();
        }
      }
    });
  };

  let onSubscriptionChange = () => {
    if (hasSubscription) {
      editSubscription();
      hasSubscription = false;
    } else {
      delete product.additions.subscription;
      setChanged();
    }
  };

  let editSubscription = () => {
    let sub = Object.assign(
      {},
      product.additions.subscription || {
        cycles: 0,
        period: 1,
        periodUnit: 1
      }
    );

    let errorDisplay = 'none';
    let error = '';
    if (!External.Website.storeSubscriptionAvailable) {
      errorDisplay = 'block';
      error = External.Website.minPlanForStoreSubscriptions ? M.SUBSCRIPTIONS_MIN_AVAILABLE_PLAN + ' ' + External.Website.minPlanForStoreSubscriptions : M.SUBSCRIPTIONS_NOT_AVAILABLE;
    }

    External.Tools.showWindow({
      title: M.EDIT_SUBSCRIPTION,
      width: 400,
      markup: External.format(External.Templates.get('productSubscriptionEditDialog'), 'errorDisplay', errorDisplay, 'error', error),
      action: {
        onShow: function(popup, context) {
          popup.bindObject('data', sub, [
            {
              property: 'cycles',
              type: 'int'
            },
            {
              property: 'period',
              type: 'int'
            },
            {
              property: 'periodUnit',
              type: 'int'
            }
          ]);
        }
      },
      buttons: [
        External.Button(External.Website.storeSubscriptionAvailable ? M.SUBMIT : M.UPGRADE_NOW, External.Button.CLASS_MIDDLE_BLUE, function(popup, context) {
          if (!External.Website.storeSubscriptionAvailable) {
            popup.close();
            External.CPanel.goToUrl('/me/settings/billing');
            return;
          }
          product.additions.subscription = sub;
          hasSubscription = product.additions.subscription && product.additions.subscription.period;
          popup.close();
          setChanged();
        }),
        External.Button.MIDDLE_CANCEL
      ]
    });
  };

  let onProductTypeChange = (markAsChanged) => {
    if (product.type === External.STORE_PRODUCT_TYPE.DIGITAL_PRODUCT) {
      productFileDisplayName = product.file || '';

      if (productFileDisplayName !== '') {
        let idx = productFileDisplayName.indexOf('/uploads/');
        if (idx >= 0) {
          productFileDisplayName = productFileDisplayName.substring(idx + 9, productFileDisplayName.length);
        }
        let parts = productFileDisplayName.split('/');
        if (parts.length === 2) {
          productFileDisplayName = parts[0] + '/' + parts[1].replace(/^[0-9]+_/, '');
        } else {
          productFileDisplayName = parts[0].replace(/^[0-9]+_/, '');
        }
        productFileDisplayName = productFileDisplayName.replace(/_/g, ' ');
      }
    } else if (product.type === External.STORE_PRODUCT_TYPE.MEMBERSHIP) {
      product.additions.memberGroups = product.additions.memberGroups || [];
      selectedMemberGroupNames = product.additions.memberGroups
      .map((groupId) => External.Website.getMemberGroupById(groupId))
      .filter((group) => group !== null)
      .map((group) => group.name);
    }

    if (markAsChanged) {
      setChanged();
    }
  };

  let editVariations = () => {
    API.Store.Products.editVariations(product, () => {
      product.variations = product.variations;
      product.combinations = product.combinations;
      setChanged();
    });
  };

  let editAdditionalQuestions = () => {
    API.Store.Products.editAdditions(product, setChanged);
  };

  let editInventory = () => {
    API.Store.Products.editInventory(product, () => {
      refreshInventoryCbx();
      setChanged();
    });
  };

  let editSeo = () => {
    API.Store.Products.editSeo(product, setChanged, () => {
      if (editorType === 'complex') {
        let blocks = grid.getContainer().find('.grid-row');
        return External.UITools.getTextWidgetsContent(blocks);
      } else {
        return External.$(textEditorMarkup).text();
      }
    });
  };

  let selectTags = () => {
    External.UITools.selectTagLabels(
      product.additions.tags || [],
      (tags) => {
        product.additions.tags = tags;
        setChanged();
      },
      true
    );
  };

  let addWidget = (event) => {
    if (grid) {
      grid.showAddWidgetDialog(event);
    }
  };

  let getPublicUrl = (useSystemDomain) => {
    let storePath = External.Website.getPageById(External.Website.STORE_PAGE_ID) === null ? '' : External.Website.getPageById(External.Website.STORE_PAGE_ID).link;
    return (useSystemDomain ? ('https://' + External.userDomain) : ('http://' + External.primaryDomain)) + '/' + storePath + '/' + product.url;
  };

  let copyLink = () => {
    let url = getPublicUrl(false);
    External.Tools.copyToClipboard(url);
    External.Tools.showCornerMessage(1000, M.COPIED_TO_CLIPBOARD, External.Tools.TOOLTIP_TYPE_SUCCESS);
  };

  let previewProduct = () => {
    External.Tools.showWebsitePreviewPopup({
      url: getPublicUrl(true),
      buttonText: M.SHOW_IN_NEW_TAB,
      onButtonAction: (pageUrl) => {
        let win = window.open(getPublicUrl(false), '_blank');
        win.focus();
      }
    });
  };

  let deleteProduct = () => {
    External.Tools.prompt(M.DEL_CONFIRM, External.escapeFormat(M.DEL_CONFIRM_EXT, 'ext', product.title), async () => {
      let result = await makeWebsiteRequest({
        action: 'deleteProduct',
        data: {
          id: product.id
        }
      });

      setChanged(false);
      External.CPanel.goToUrl(External.contextPath + '/me/store/products');
    });
  };

  let cloneProduct = async () => {
    await saveProduct(true);
  };

  let saveProduct = async (clone = false, showConfirmationMessage = true) => {
    if (product.type === External.STORE_PRODUCT_TYPE.MEMBERSHIP && (!product.additions.memberGroups || product.additions.memberGroups.length === 0)) {
      External.CPanel.showError(M.ERR_SELECT_MEMBER_GROUPS);
      return;
    }

    let description;
    if (grid) {
      description = {
        simple: false,
        widgets: grid.getSaveObject().widgets
      };
    } else {
      description = {
        simple: true,
        text: textEditorMarkup
      };
    }
    product.description = description;

    if (!hasThankYouPage) {
      delete product.additions.thankyouPage;
    }

    if (clone) {
      product.id = -1;
      product.url += '-copy';
    }

    if (product.type === External.STORE_PRODUCT_TYPE.MEMBERSHIP || product.type === External.STORE_PRODUCT_TYPE.DIGITAL_PRODUCT) {
      product.combinations = [];
      product.variations = [];
    }

    if (!isTrackingInventory) {
      product.quantity = '';
      product.combinations.forEach((combination) => {
        combination.quantity = '';
      });
    }

    saving = true;
    let localeSettings = getLocaleSettings();
    let locale = localeSettings.currentLocale;
    if (product.id < 1) {
      locale = localeSettings.defaultLocale;
    }
    External.Tools.makeWebsiteRequest({
      action: 'saveStoreProduct',
      method: 'post',
      data: {
        product: JSON.stringify(product),
        locale: locale
      },
      onOk: function(result) {
        let data = result.data;
        if (data === "NO_STORE_ITEMS_AVAILABLE") {
          External.UITools.showNoMoreProductsDialog(() => {
            setChanged(false);
            External.CPanel.goToUrl("/me/settings/billing");
          });
        } else {
          setChanged(false);
          if (onSave) {
            onSave(result.data);
          } else if (showConfirmationMessage) {
            External.Tools.showCornerMessage(3000, M.UPDATED, External.Tools.TOOLTIP_TYPE_SUCCESS);
            External.CPanel.goToUrl(External.contextPath + '/me/store/products/' + result.data.id);
          }
        }
      },
      always: function() {
        saving = false;
      }
    });
  };

  let discardChanges = () => {
    setChanged(false);
    if (inPopup) {
      editorType = 'invalid';
      reload();
    } else {
      External.CPanel.goToUrl(External.CPanel.currentPage, true, newProductType);
    }
  };

  $: {
    if (product) {
      showingSingleVariantOptions = true;

      if (product.type !== External.STORE_PRODUCT_TYPE.MEMBERSHIP && product.type !== External.STORE_PRODUCT_TYPE.DIGITAL_PRODUCT && product.combinations.length) {
        showingSingleVariantOptions = false;
        cheapestVariation = product.combinations[0].price;
        for (let idx = 0; idx < product.combinations.length; ++idx) {
          let combination = product.combinations[idx];
          cheapestVariation = Math.min(cheapestVariation, combination.onSale ? combination.salePrice : combination.price);
        }
      }
      refreshInventoryCbx();
    }
  }

  onDestroy(() => {
    External.Tools.removeShortcutListener('s');
  });

  let reload = async () => {
    if (id === 0) {
      init({
        markup: false,
        product: {
          id: -1,
          type: newProductType
        }
      });
      onLoad();
    } else {
      let result = await makeWebsiteRequest({
        action: 'loadStoreProduct',
        data: {
          id: id,
          locale: getActiveLocale()
        }
      });

      if (result.data === null) {
        External.CPanel.goToUrl(External.contextPath + '/me/store/products');
        return;
      }
      init(result.data);
      onLoad();
    }
  };

  let showTextSuggestions = () => {
    API.Common.showAiTextCompletionOptions(showTextSuggestionsBtn, {
      elementType: 'heading',
      phrase: product.title || '',
      hasExtraBtn: true,
      extraBtnText: M.TEXT_ASSIST_DESCRIPTION_FROM_TITLE,
      extraBtnType: 'PRODUCT_DESCRIPTION',
      onComplete: (text, type) => {
        if (text) {
          if (type === 'PRODUCT_DESCRIPTION') {
            editorType = null;
            setTimeout(() => {
              textEditorMarkup = '<p style="color: #888; line-height: 1.5">' + text + '</p>';
              setDescriptionType('simple');
            }, 10);
          } else {
            product.title = text;
          }
        }
      }
    });
  };

  onMount(async () => {
    External.Tools.addShortcutListener('s', function(isShiftPressed, isAltPressed) {
      if (!isShiftPressed && External.CPanel.haveUnsavedChanges) {
        saveProduct(false);
      }
    });
    await reload();
  });
</script>

{#if product}
  <div class="account-settings store-products" style="overflow-x: hidden;">
    <div class="inner-wrapper member-info-wrapper">
      {#if !inPopup}
        <div class="top-background-wrapper wide-top-background" style="padding: 20px 0;">
          <div class="top-background" style="max-width:1150px;">
            <div class="text-wrapper flex align-center space-between">
              <div class="flex align-center flex-none">
                <a rel="tab" href="/me/store/products" class="back-to-next-step flex-none"
                   style="width: auto; margin-bottom: 0; margin-right: 20px;">
                  &larr; {M.BACK_TO_PRODUCTS}</a>
              </div>
              <div class="store-product-buttons">
                {#if product.id > 0}
                  <a on:click={cloneProduct} class="f-right middle-button blue"
                     style="height:39px; line-height:39px;">{M.COPY_PRODUCT}</a>
                  <a on:click={deleteProduct} class="f-right middle-button blue mr-5"
                     style="height:39px; line-height:39px;">
                    {M.DELETE_PRODUCT}
                  </a>
                  <a on:click={previewProduct} class="f-right middle-button blue mr-5"
                     style="height:39px; line-height:39px;">{M.PREVIEW}</a>
                  <a on:click={copyLink} class="f-right border-button mr-5 rounded-effect"
                     style="height:39px; line-height:35px; box-sizing: border-box;">
                    {M.COPY_LINK_CAPITALIZED}
                  </a>
                {/if}
              </div>
            </div>
          </div>
        </div>
      {/if}
      <div class="content-wrapper" style="max-width: 1150px;">
        <div class="sections-wrapper profile-inner-wrapper" style="margin-bottom: 80px; margin-top: 40px;">
          <div class="member-page">
            <div class="profile-wrapper admin-product-wrapper" style="margin-top: 0;">


              <!-- New Visual Idea --

              <div
                class="col20 f-left grid-content-store theme product-page-style-image-left-big no-table full-heightt reverse-contentt skewed-background with-shadoww floating-buy-button">
                <div class="product-wrapper">
                  <div class="product-content-wrapper col20 center-content">
                    <div class="product-photo-container side-photoss with-padding style-2">
                      <div class="product-main-photo">
                        <img class="none" src="https://content.app-sources.com/s/24444282803329/uploads/a-products/bag-2-7710990.png">
                        <img src="https://content.app-sources.com/s/24444282803329/uploads/a-juice/4-4332104.jpg">
                        <span class="col20 f-left t-center mt-25 mb-10 fs-16">+5 Images</span>
                        <div class="col20 f-left t-center">
                          <a href="" class="middle-button f-none inline-block blue">Manage Images</a>
                        </div>
                      </div>
                    </div>
                    <div class="product-body-container">
                      <div class="product-body-container-inner">
                        <div class="col20 f-left brand-wrapper flex align-center space-between">
                          <ul class="breadcrumb">
                            <li><a href="/" class="highlightColor">Home</a><span class="bread-separator">/</span></li>
                            <li><a href="/store" class="highlightColor">Store</a><span class="bread-separator">/</span></li>
                            <li>
                              <p href="/store/bump" class="highlightColor">Bump</p>
                              <span class="bread-separator">/</span>
                            </li>
                          </ul>
                          <a href="" class="share-button flex align-center rounded-effect">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                              <path d="M0 0h24v24H0V0z" fill="none"></path>
                              <path
                                d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92zM18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 7.02c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"></path>
                            </svg>
                            <span>Share</span>
                          </a>
                        </div>
                        <script type="text/javascript">
                          $('#autoresizing').on('input', function() {
                            this.style.height = 'auto';

                            this.style.height = (this.scrollHeight) + 'px';
                          });
                        </script>
                        <h1 itemprop="name"><textarea id="autoresizing" type="text" name="productTitle" class="modify"
                                                      placeholder="Enter product name..."></textarea></h1>
                        <div class="col20 f-left product-stars flex align-center">
                          <div class="product-stars-inner">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px" class="full-star">
                              <path d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"></path>
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px" class="full-star">
                              <path d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"></path>
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px" class="full-star">
                              <path d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"></path>
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                              <path d="M0 0h24v24H0V0z" fill="none"></path>
                              <path
                                d="M12 7.13l.97 2.29.47 1.11 1.2.1 2.47.21-1.88 1.63-.91.79.27 1.18.56 2.41-2.12-1.28-1.03-.64-1.03.62-2.12 1.28.56-2.41.27-1.18-.91-.79-1.88-1.63 2.47-.21 1.2-.1.47-1.11.97-2.27M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"></path>
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                              <path d="M0 0h24v24H0V0z" fill="none"></path>
                              <path
                                d="M12 7.13l.97 2.29.47 1.11 1.2.1 2.47.21-1.88 1.63-.91.79.27 1.18.56 2.41-2.12-1.28-1.03-.64-1.03.62-2.12 1.28.56-2.41.27-1.18-.91-.79-1.88-1.63 2.47-.21 1.2-.1.47-1.11.97-2.27M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"></path>
                            </svg>
                          </div>
                          <a href="" class="product-reviews-link">84 Reviews</a>
                        </div>

                        <div class="product-price">
                          <a href="" class="store-product-settings-btn rounded-effect system-bg-color">
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
                              <path d="M0 0h24v24H0V0z" fill="none" />
                              <path
                                d="M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.09-.16-.26-.25-.44-.25-.06 0-.12.01-.17.03l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.06-.02-.12-.03-.18-.03-.17 0-.34.09-.43.25l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.09.16.26.25.44.25.06 0 .12-.01.17-.03l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.06.02.12.03.18.03.17 0 .34-.09.43-.25l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zm-1.98-1.71c.04.31.05.52.05.73 0 .21-.02.43-.05.73l-.14 1.13.89.7 1.08.84-.7 1.21-1.27-.51-1.04-.42-.9.68c-.43.32-.84.56-1.25.73l-1.06.43-.16 1.13-.2 1.35h-1.4l-.19-1.35-.16-1.13-1.06-.43c-.43-.18-.83-.41-1.23-.71l-.91-.7-1.06.43-1.27.51-.7-1.21 1.08-.84.89-.7-.14-1.13c-.03-.31-.05-.54-.05-.74s.02-.43.05-.73l.14-1.13-.89-.7-1.08-.84.7-1.21 1.27.51 1.04.42.9-.68c.43-.32.84-.56 1.25-.73l1.06-.43.16-1.13.2-1.35h1.39l.19 1.35.16 1.13 1.06.43c.43.18.83.41 1.23.71l.91.7 1.06-.43 1.27-.51.7 1.21-1.07.85-.89.7.14 1.13zM12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                            </svg>
                          </a>
                          <span class="current-price" data-role="currentPrice">$99.00</span>
                          <span class="old-price none" data-role="oldPrice"></span>
                          <span class="price-discount-tag">-28%</span>
                        </div>
                        <div class="col20 f-left add-product-section">
                          <a href="" class="add-button-product system-bg-color"><span>+</span></a>
                        </div>
                        <div class="addtocart-wrapper">
                          <div class="col20 flex add-to-cart-inner">
                            <div class="qty-wrapper product-qty-wrapper flex-none">
                              <div class="number-selector f-left flex align-center">
                                <a class="number-pad flex-none">-</a>
                                <input class="cart-row-quantity" type="text" size="4" step="1" max="9999" min="1" value="3">
                                <a class="number-pad flex-none">+</a>
                              </div>
                            </div>
                            <div class="add-to-wrapper flex-1 col20">
                              <a data-role="addToCart" data-process="buy" class="primaryColor-bg2 add-to-cart t-center col20"
                                 style="background: #222;">Buy Now</a>
                              <div class="sold-out-btn none" data-role="soldOut">Sold Out</div>
                            </div>
                          </div>
                        </div>
                        <div class="col20 f-left add-product-section">
                          <a href="" class="add-button-product system-bg-color"><span>+</span></a>
                        </div>
                        <div class="product-description">

                        </div>
                        <div class="col20 f-left">
                          <span class="product-characteristics-wrapper col20 f-left flex align-center mb-5">Brand: <a
                            class="product-characteristics rounded-effect">Adidas</a></span>
                          <span class="product-characteristics-wrapper col20 f-left flex align-center mb-5">Material: <a
                            class="product-characteristics rounded-effect">Wood</a><a class="product-characteristics rounded-effect">Iron</a></span>
                        </div>
                        <div class="share-section" style="display: none;">
                          <span>Share</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col20 f-left t-center mt-25 flex align-center justify-center">
                <a href="" class="big-button blue inline-block f-none">Product Settings</a>
                <a href="" class="system-bg-color ml-5 big-circle-button rounded-effect flex align-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path
                      d="M7 16c.55 0 1 .45 1 1 0 1.1-.9 2-2 2-.17 0-.33-.02-.5-.05.31-.55.5-1.21.5-1.95 0-.55.45-1 1-1M18.67 3c-.26 0-.51.1-.71.29L9 12.25 11.75 15l8.96-8.96c.39-.39.39-1.02 0-1.41l-1.34-1.34c-.2-.2-.45-.29-.7-.29zM7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3z" />
                  </svg>
                </a>
                <a href="" class="big-button gray inline-block f-none with-icon ml-5 rounded-effect">Digital File</a>
              </div>

              <div class="col20 f-left mt-25 mb-25 t-center add-section-wrapper">
                <a class="small-button blue with-icon rounded-effect f-none inline-block">Add Section</a>
              </div>

              <div class="col20 f-left mb-25 store-product-section">
                <ul class="product-tabs col20 f-left t-center">
                  <li><a href="" class="active">Overview</a></li>
                  <li><a href="">Specs</a></li>
                  <li><a href="">Reviews</a></li>
                </ul>
                <div class="col20 f-left reviews-outer-wrapper">
                  <div class="col20 f-left reviews-header">
                    <div class="review-left f-left col10">
                      <div class="col20 f-left mb-15 stars-big">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path
                            d="M12 7.13l.97 2.29.47 1.11 1.2.1 2.47.21-1.88 1.63-.91.79.27 1.18.56 2.41-2.12-1.28-1.03-.64-1.03.62-2.12 1.28.56-2.41.27-1.18-.91-.79-1.88-1.63 2.47-.21 1.2-.1.47-1.11.97-2.27M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path
                            d="M12 7.13l.97 2.29.47 1.11 1.2.1 2.47.21-1.88 1.63-.91.79.27 1.18.56 2.41-2.12-1.28-1.03-.64-1.03.62-2.12 1.28.56-2.41.27-1.18-.91-.79-1.88-1.63 2.47-.21 1.2-.1.47-1.11.97-2.27M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
                        </svg>
                      </div>
                      <h3>84 Reviews</h3>
                      <span>4 out of 5 stars</span>
                    </div>

                    <div class="review-right f-left col10">
                      <div class="col20 f-left flex align-center review-line">
                        <span class="flex-none">5 Stars</span>
                        <div class="progress-line flex-1">
                          <div class="progress-line-complete" style="width: 76%;"></div>
                        </div>
                        <span class="flex-none">74%</span>
                      </div>
                      <div class="col20 f-left flex align-center review-line">
                        <span class="flex-none">4 Stars</span>
                        <div class="progress-line flex-1">
                          <div class="progress-line-complete" style="width: 16%;"></div>
                        </div>
                        <span class="flex-none">16%</span>
                      </div>
                      <div class="col20 f-left flex align-center review-line">
                        <span class="flex-none">3 Stars</span>
                        <div class="progress-line flex-1">
                          <div class="progress-line-complete" style="width: 2%;"></div>
                        </div>
                        <span class="flex-none">2%</span>
                      </div>
                      <div class="col20 f-left flex align-center review-line">
                        <span class="flex-none">2 Stars</span>
                        <div class="progress-line flex-1">
                          <div class="progress-line-complete" style="width: 8%;"></div>
                        </div>
                        <span class="flex-none">8%</span>
                      </div>
                      <div class="col20 f-left flex align-center review-line">
                        <span class="flex-none">1 Stars</span>
                        <div class="progress-line flex-1">
                          <div class="progress-line-complete"></div>
                        </div>
                        <span class="flex-none">0%</span>
                      </div>
                    </div>
                  </div>
                  <div class="col20 f-left ratings-outer-wrapper">
                    <div class="col16 f-left rating-wrapper">
                      <h4>The Best Platform Ever!</h4>
                      <span class="review-author">by Stan Petrov on 20 March, 2020</span>
                      <div class="stars-small f-left col20">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      </div>
                      <span class="review-comment">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</span>
                    </div>

                    <div class="col16 f-left rating-wrapper">
                      <h4>The Best Platform Ever!</h4>
                      <span class="review-author">by Stan Petrov on 20 March, 2020</span>
                      <div class="stars-small f-left col20">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      </div>
                      <span class="review-comment">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</span>
                    </div>


                    <div class="col16 f-left rating-wrapper">
                      <h4>The Best Platform Ever!</h4>
                      <span class="review-author">by Stan Petrov on 20 March, 2020</span>
                      <div class="stars-small f-left col20">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      </div>
                      <span class="review-comment">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</span>
                    </div>


                  </div>
                </div>
              </div>

              -- END of New Visual Idea -->


              <div class="product-content f-left box-shadow">
                <div class="box f-left col20 w-bg">
                  <section>
                    <h5 class="info relative">
                      {M.STORE_PRODUCT_TITLE}
                      {#if External.Website.textAssistEnabled}
                        <a class="ai-help-btn f-left system-bg-color rounded-effect" bind:this={showTextSuggestionsBtn}
                           on:click={showTextSuggestions}>
                          <TextAssistIcon />
                        </a>
                      {/if}
                    </h5>
                    <input type="text" placeholder={M.ENTER_PRODUCT_NAME} bind:value={product.title}
                           on:keyup={onTitleChange} />
                  </section>
                  <section>
                    <h5 class="info">
                      {M.DESCRIPTION}
                      <div class="inline-switch f-right mb-5">
                        <a class="inline-switch-item rounded-effect" class:active={editorType === "simple"}
                           on:click={() => setDescriptionType("simple")}>{M.SIMPLE_EDITOR}</a>
                        <a class="inline-switch-item rounded-effect" class:active={editorType === "complex"}
                           on:click={() => setDescriptionType("complex")}>{M.DRAG_DROP_EDITOR}</a>
                      </div>
                    </h5>
                    {#if editorType === "simple"}
                      <div class="product-description-grid col20 f-left"
                           style="font-family: 'Open Sans'; box-sizing: border-box;">
                        <TextEditor onChange={onTextEditorChange} meta={textEditorOptions} data={textEditorMarkup} />
                      </div>
                    {:else if editorType === "complex"}
                      <div class="product-description-grid desktop-view flex flex-col" use:setupGrid>
                        {@html builderMarkup}
                      </div>
                      <div class="widgetNav flex flex-row justify-center">
                        <a class="add-widget-bottom block flex-none mt-15"
                           on:click={(event) => addWidget(event)}>{M.ADD_WIDGET_TITLE}</a>
                      </div>
                    {/if}
                  </section>
                  <section style="margin-bottom: 0">
                    {#if !showingSingleVariantOptions}
                      <a class="product-price-field mt-15" on:click={editVariations}>
                        {M.FROM_RANGE}
                        <span class="price-first">
                          {External.Website.storeCurrencyPositionAtLeft ? External.Website.storeCurrencySymbol : cheapestVariation.toFixed(2)}
                        </span>
                        <span class="price-second">
                          {External.Website.storeCurrencyPositionAtLeft ? cheapestVariation.toFixed(2) : External.Website.storeCurrencySymbol}
                        </span>
                        - {M.PRICING_DEPENDS}
                      </a>
                    {/if}
                    <div class="col20 f-left single-price-wrapper">
                      <div class="product-price-options">
                        {#if showingSingleVariantOptions}
                          <section class="third-section">
                            <h4 class="info">{M.STORE_PRODUCT_PRICE}</h4>
                            <div class="symbol-input price-input"
                                 class:left-sign={External.Website.storeCurrencyPositionAtLeft}
                                 class:right-sign={!External.Website.storeCurrencyPositionAtLeft}>
                              <span class="symbol-sign">{External.Website.storeCurrencySymbol}</span>
                              <input type="number" min="0.01" step="0.01" placeholder="0.00" bind:value={product.price}
                                     on:change={setChanged} />
                            </div>
                            <div class="checkbox-wrapper f-left col20 mt-15 mb-15">
                              <input id="product-on-sale" class="css-checkbox" type="checkbox"
                                     bind:checked={product.onSale} on:change={setChanged} />
                              <label for="product-on-sale" class="css-label button-tooltip">{M.PRODUCT_ON_SALE}</label>
                            </div>
                          </section>
                          {#if product.onSale}
                            <section class="third-section">
                              <h4 class="info">{M.STORE_PRODUCT_SALE_PRICE}</h4>
                              <div class="symbol-input price-input"
                                   class:left-sign={External.Website.storeCurrencyPositionAtLeft}
                                   class:right-sign={!External.Website.storeCurrencyPositionAtLeft}>
                                <span class="symbol-sign">{External.Website.storeCurrencySymbol}</span>
                                <input type="number" min="0.01" step="0.01" placeholder="0.00"
                                       bind:value={product.salePrice} on:change={setChanged} />
                              </div>
                            </section>
                          {/if}
                        {/if}
                        <section style="margin-bottom: 0;">
                          <div class="checkbox-wrapper f-left col20">
                            <input id="add-subscription" class="css-checkbox" type="checkbox"
                                   bind:checked={hasSubscription} on:change={onSubscriptionChange} />
                            <label for="add-subscription" class="css-label">
                              {M.SUBSCRIPTION_PRODUCT}
                              <span>{M.SUBSCRIPTION_PRODUCT_DESC}</span>
                            </label>
                          </div>
                          {#if hasSubscription && product.additions.subscription}
                            <div class="col20 f-left">
                              <section class="mt-15 product-price-options" style="background: #fff;">
                                <span class="f-18">
                                  {#if product.additions.subscription.period === 1}
                                    {product.additions.subscription.periodUnit === 1 ? M.RENEWS_EVERY_MONTH : M.RENEWS_EVERY_WEEK}
                                  {:else}
                                    {External.format(
                                      product.additions.subscription.periodUnit === 1 ? M.RENEWS_EVERY_MONTHS : M.RENEWS_EVERY_WEEKS,
                                      "no",
                                      product.additions.subscription.period
                                    )}
                                  {/if}
                                  {#if product.additions.subscription.cycles > 0}
                                    ({product.additions.subscription.cycles} {M.PAYMENTS})
                                  {/if}
                                </span>
                                <a class="btn system-text-color" style="margin-left: 5px; display: inline-block;"
                                   on:click={editSubscription}>
                                  {M.CHANGE_UPPER}
                                </a>
                              </section>
                            </div>
                          {/if}
                        </section>
                      </div>
                      {#if showingSingleVariantOptions}
                        <section class="half-section">
                          <h5 class="info">{M.SKU_EXTENDED}</h5>
                          <input type="text" bind:value={product.sku} on:change={setChanged} />
                        </section>
                        {#if product.type === External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT}
                          <section class="half-section weight-section f-right" style="margin-right: 0;">
                            <h5 class="info">{M.WEIGHT}</h5>
                            <div class="symbol-input right-sign">
                              <span class="symbol-sign">{External.Website.storeWeightUnit}</span>
                              <input type="number" step="0.01" placeholder="1.00" bind:value={product.weight}
                                     on:change={setChanged} />
                            </div>
                          </section>
                        {/if}
                      {/if}
                    </div>
                    {#if showingSingleVariantOptions}
                      {#if product.type === External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT}
                      <div class="col20 f-left single-price-wrapper">
                        <h5 class="col20 f-left" style="margin-bottom: 0;">{M.DIMENSIONS}</h5>
                        <div class="product-price-options">
                            <section class="third-section">
                              <h5 class="info">{M.WIDTH}</h5>
                              <div class="symbol-input right-sign">
                                <span class="symbol-sign">{External.Website.storeDimensionsUnit}</span>
                                <input type="number" min="0.01" step="0.1" placeholder="0" bind:value={product.width}
                                       on:change={setChanged}/>
                              </div>
                            </section>
                          <section class="third-section">
                            <h5 class="info">{M.HEIGHT}</h5>
                            <div class="symbol-input right-sign">
                              <span class="symbol-sign">{External.Website.storeDimensionsUnit}</span>
                              <input type="number" min="0.01" step="0.1" placeholder="0" bind:value={product.height}
                                     on:change={setChanged}/>
                            </div>
                          </section>
                          <section class="third-section">
                            <h5 class="info">{M.LENGTH}</h5>
                            <div class="symbol-input right-sign">
                              <span class="symbol-sign">{External.Website.storeDimensionsUnit}</span>
                              <input type="number" min="0.01" step="0.1" placeholder="0" bind:value={product.length}
                                     on:change={setChanged}/>
                            </div>
                          </section>
                        </div>
                      </div>
                      {/if}
                    {/if}
                  <section class="half-section">
                    <h5 class="col20 f-left" style="margin-bottom: 0;">{M.TYPE_OF_PRODUCT}</h5>
                    <select class="col20 f-left" bind:value={product.type} on:change={() => onProductTypeChange(true)}>
                      <option value="physical">{M.STORE_PRODUCT_PHYSICAL}</option>
                      <option value="digital">{M.STORE_PRODUCT_DIGITAL}</option>
                      <option value="service">{M.STORE_PRODUCT_SERVICE}</option>
                      <option value="membership">{M.STORE_PRODUCT_MEMBERSHIP}</option>
                      <!--
                      <option>Listing</option>
                      <option>Custom Page Design</option>
                      -->
                    </select>
                  </section>
                  {#if product.type === External.STORE_PRODUCT_TYPE.DIGITAL_PRODUCT}
                    <section>
                      <h4 class="info">{M.PRODUCT_FILE}</h4>
                      <div class="col20 f-left">
                        <div class="product-price-options" style="margin: 0;">
                          <section>
                            <input class="select-like" readonly="readonly" value={productFileDisplayName}
                                   on:click={selectFile} />
                          </section>
                          <section class="mt-15">
                            <span style="line-height: 1.3; color: #888;">{M.PRODUCT_FILE_DESC}</span>
                          </section>
                        </div>
                      </div>
                    </section>
                  {/if}
                  {#if product.type === External.STORE_PRODUCT_TYPE.MEMBERSHIP}
                    <section>
                      <h4 class="info roboto">{M.MEMBER_GROUPS}</h4>
                      <div class="col20 f-left">
                        <div class="product-price-options" style="margin: 0;">
                          <section class="multiple-items-picker">
                            <ul class="groups" style="padding: 20px;">
                              <li class="permanent-item"><a on:click={selectMemberGroups}
                                                            class="middle-button gray">{M.SELECT_LBL}</a></li>
                              {#each selectedMemberGroupNames as name}
                                <li>{name}</li>
                              {/each}
                            </ul>
                          </section>
                          <section class="mt-5">
                            <span style="line-height: 1.3; color: #888;">{M.MEMBER_GROUPS_SELECT_DESC}</span>
                          </section>
                        </div>
                      </div>
                    </section>
                  {/if}
                  </section>
                </div>
              </div>
              <div class="f-right product-sidebar box-shadow">
                <div class="multi-image-picker">
                  {#if product.images.length}
                    <div on:click={pickImages}>
                      <img alt=""
                           src={External.getThumbnailsFromBase(product.images[0].base, "640x480") + product.images[0].name} />
                    </div>
                  {:else}
                    <div class="no-image-preview" on:click={pickImages} style="height: 160px" />
                  {/if}
                  {#if product.images.length > 1}
                    <span class="col20 f-left t-center mt-10">
                      +{product.images.length - 1}
                      {product.images.length === 2 ? M.IMAGE : M.IMAGES}
                    </span>
                  {/if}
                  <div class="col20 f-left t-center mt-15">
                    <a class="middle-button f-none inline-block blue with-icon" on:click={pickImages}>{M.ADD_IMAGES}</a>
                  </div>
                </div>
                <section class="multiple-items-picker f-left mt-25" style="margin-bottom: 0">
                  <h4 class="info roboto mb-15">{M.PRODUCT_CATEGORIZATION}</h4>
                  <div class="col20 f-left gray-section mb-5">
                    <div class="col20 f-left center-vertically flex-col">
                      <h4 class="roboto f-16 mb-15">
                        {#if product.categories.length === 0}
                          {M.NO_CATEGORIES}
                        {:else}
                          {product.categories.length === 1 ? M.CATEGORY_SELECTED : External.WebPlatform.format(M.CATEGORIES_SELECTED, "no", product.categories.length)}
                        {/if}
                      </h4>
                      <a on:click={editCategories} class="middle-button blue">{M.EDIT_CATEGORIES}</a>
                    </div>
                  </div>
                </section>
                <div class="checkbox-wrapper f-left mb-15 mt-10">
                  <input id="store-product-hidden" class="css-checkbox no-toggle" type="checkbox"
                         bind:checked={product.hidden} on:change={setChanged} />
                  <label for="store-product-hidden" class="css-label no-toggle">{M.HIDDEN_PRODUCT}</label>
                </div>
                <section>
                  <h5 class="info mb-5">{M.STORE_PRODUCT_URL}</h5>
                  <input type="text" style="font-size: 14px;" bind:value={product.url} on:keypress={setUrl} />
                </section>
                <section class="t-center">
                  <a on:click={editSeo} class="middle-button blue mt-25 f-none inline-block">{M.SEO_SETTINGS}</a>
                </section>
              </div>

              <div class="col20 f-left mt-25 product-options-list mb-35">
                <div class="box-shadow third-section-global f-left t-center bg-white padding-45">
                  <span class="material-icons-outlined fingerprint-icon" style="font-size: 26px;"> inventory </span>
                  <h2 class="col20 f-left mt-25 t-center roboto f-22">{M.TRACK_INVENTORY}</h2>
                  <span class="col20 f-left mt-10 t-center light-gray-text">
                    {M.TRACK_INVENTORY_DESC}
                  </span>
                  <div class="col20 f-left mt-15">
                    <div class="onoffswitch onoffswitch-small" style="margin: 0 auto;">
                      <input type="checkbox" class="onoffswitch-checkbox modify" id="inventoryCbx" name="inventoryCbx"
                             bind:checked={isTrackingInventory} />
                      <label class="onoffswitch-label" for="inventoryCbx">
                        <span class="onoffswitch-inner" />
                        <span class="onoffswitch-switch" />
                      </label>
                    </div>
                  </div>
                  {#if isTrackingInventory}
                    <div class="col20 f-left t-center" transition:slide|local>
                      <a on:click={editInventory}
                         class="middle-button f-none inline-block blue mt-15">{M.EDIIT_INVENTORY}</a>
                    </div>
                  {/if}
                </div>
                {#if product.type !== External.STORE_PRODUCT_TYPE.MEMBERSHIP && product.type !== External.STORE_PRODUCT_TYPE.DIGITAL_PRODUCT}
                  <div class="box-shadow third-section-global f-left t-center bg-white padding-45">
                    <span class="material-icons-outlined fingerprint-icon"
                          style="font-size: 26px;"> invert_colors </span>
                    <h2 class="col20 f-left mt-25 t-center roboto f-22">{M.VARIATIONS}</h2>
                    <span class="col20 f-left mt-10 t-center light-gray-text">
                      {M.VARIATIONS_DESC}
                    </span>
                    <div class="col20 f-left t-center mt-15">
                      <a on:click={editVariations}
                         class="middle-button f-none inline-block blue">{M.EDIT_VARIATIONS}</a>
                    </div>
                  </div>
                {/if}

                <div class="box-shadow third-section-global f-left t-center bg-white padding-45">
                  <span class="material-icons-outlined fingerprint-icon"
                        style="font-size: 26px;"> insert_drive_file </span>
                  <h2 class="col20 f-left mt-25 t-center roboto f-22">{M.CUSTOM_THANK_YOU_PAGE}</h2>
                  <span class="col20 f-left mt-10 t-center light-gray-text">
                    {M.CUSTOM_TY_DESC}
                  </span>
                  <div class="col20 f-left mt-15">
                    <div class="onoffswitch onoffswitch-small" style="margin: 0 auto;">
                      <input type="checkbox" class="onoffswitch-checkbox modify" id="showThankYouPage"
                             name="showThankYouPage" bind:checked={hasThankYouPage} />
                      <label class="onoffswitch-label" for="showThankYouPage">
                        <span class="onoffswitch-inner" />
                        <span class="onoffswitch-switch" />
                      </label>
                    </div>
                  </div>
                  {#if hasThankYouPage}
                    <div class="col20 f-left" transition:slide|local>
                      <section class="mt-15">
                        <span>{M.PRODUCT_THANK_YOU_MESSAGE}</span>
                      </section>
                      <section class="mt-10">
                        <input class="select-like maxw-400" readonly="readonly" value={thankYouPageFriendlyText}
                               on:click={setThankYouPage} />
                      </section>
                    </div>
                  {/if}
                </div>

                <div class="box-shadow third-section-global f-left t-center bg-white padding-45">
                  <span class="material-icons-outlined fingerprint-icon" style="font-size: 26px;"> help_outline </span>
                  <h2 class="col20 f-left mt-25 t-center roboto f-22">{M.ADDITIONAL_QUESTIONS}</h2>
                  <span class="col20 f-left mt-10 t-center light-gray-text">
                    {M.ADDITIONAL_QUESTIONS_DESC}
                  </span>
                  <div class="col20 f-left t-center mt-15">
                    <a on:click={editAdditionalQuestions}
                       class="middle-button f-none inline-block blue">{M.EDIT_QUESTIONS}</a>
                  </div>
                </div>

                <div class="box-shadow third-section-global f-left t-center bg-white padding-45">
                  <span class="material-icons-outlined fingerprint-icon" style="font-size: 26px;"> label </span>
                  <h2 class="col20 f-left mt-25 t-center roboto f-22">{M.TAG_CUSTOMERS}</h2>
                  <span class="col20 f-left mt-10 t-center light-gray-text">
                    {M.TAG_CUSTOMERS_DESC}
                  </span>

                  <div class="col20 f-left t-center mt-15">
                    <a on:click={selectTags} class="middle-button f-none inline-block blue">{M.EDIT_TAGS}</a>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      {#if changed}
        <div
          class="flex align-center justify-center floating-lane floating-lane-store floating-lane-product flex-none"
          style={inPopup ? "left: auto; right: 0; width: 1200px" : ""}>
          <a class="middle-button blue flex-none" class:disabled={saving}
             on:click={() => saveProduct(false)}>{M.SAVE_CHANGES}</a>
          <a class="text-button f-none inline-block ml-10" on:click={discardChanges}>{M.DISCARD_CHANGES_REFRESH}</a>
        </div>
      {/if}
    </div>
  </div>
{/if}
